/** Records the project open in the sidebar's props and keeps the person's recent list, cached locally and saved to their preferences row. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../utils/supabase/client';
import {
  fetchRecent,
  normaliseRecent,
  recordRecent,
  saveRecent,
  type RecentClient,
  type RecentItem,
} from '../../shared-ui/navigation/recentItems';

const client = supabase as unknown as RecentClient;
// Coalesces hops between a project's tabs into one write.
const SAVE_DELAY_MS = 1500;

const NONE: RecentItem[] = [];

const cacheKey = (userId: string) => `quantis:recent:v1:${userId}`;

function readCache(userId: string): RecentItem[] {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw ? normaliseRecent({ items: JSON.parse(raw) }) : [];
  } catch {
    return [];
  }
}

function writeCache(userId: string, items: RecentItem[]): void {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(items));
  } catch { /* the cache only speeds up first paint */ }
}

export interface OpenProject {
  id: string;
  name: string;
  section?: string;
}

export function openRecentProject(item: RecentItem): void {
  window.dispatchEvent(new CustomEvent('app:navigate', {
    detail: { view: 'project-detail', projectId: item.id, projectName: item.title, projectTab: item.section },
  }));
}

export function useRecentProjects(userId: string | undefined, open: OpenProject | null): RecentItem[] {
  const [state, setState] = useState(() => ({ owner: userId, items: userId ? readCache(userId) : [] }));
  // A different account starts from its own cache, reset during render rather than after it.
  if (state.owner !== userId) setState({ owner: userId, items: userId ? readCache(userId) : [] });
  const items = state.owner === userId ? state.items : NONE;
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const setItems = useCallback((owner: string, next: RecentItem[]) => {
    itemsRef.current = next;
    setState({ owner, items: next });
  }, []);
  const pendingRef = useRef<{ timer: ReturnType<typeof setTimeout>; userId: string; items: RecentItem[] } | null>(null);
  const localEditsRef = useRef(0);

  const flush = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingRef.current = null;
    // The merge writes to whoever is signed in, so a save left over from an account switch is dropped.
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user.id === pending.userId) void saveRecent(client, pending.items);
    });
  }, []);

  // Another device may have opened something since; a visit recorded here while the read was in flight wins over it.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      const editsAtStart = localEditsRef.current;
      // No session, no row to read (and no request at all where none is signed in).
      void supabase.auth.getSession().then(({ data }) => (
        data.session?.user.id === userId ? fetchRecent(client, userId) : null
      )).then((stored) => {
        if (cancelled || !stored || localEditsRef.current !== editsAtStart) return;
        setItems(userId, stored);
        writeCache(userId, stored);
      });
    };
    refresh();
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', refresh);
      flush();
    };
  }, [userId, flush, setItems]);

  const openId = open?.id;
  const openName = open?.name;
  const openSection = open?.section;
  useEffect(() => {
    if (!userId || !openId || !openName) return;
    const current = itemsRef.current;
    const next = recordRecent(current, { kind: 'project', id: openId, title: openName, section: openSection });
    if (next === current) return;
    localEditsRef.current += 1;
    setItems(userId, next);
    writeCache(userId, next);
    if (pendingRef.current) clearTimeout(pendingRef.current.timer);
    pendingRef.current = { userId, items: next, timer: setTimeout(flush, SAVE_DELAY_MS) };
  }, [userId, openId, openName, openSection, flush, setItems]);

  return items;
}
